// src/components/CaseListFilters/index.jsx
// Shared search bar + filter modal for the Walk-In and Pledge Release case lists.
import React, { useEffect, useState } from 'react';
import { Input, Select, Modal, DatePicker, Divider, Button, InputNumber, Space, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

// Customer-side fields are resolved on the backend (utils/caseSearch.js).
export const COMMON_SEARCH_FIELDS = [
  { value: 'all', label: 'All Fields', placeholder: 'Search case ID, name, phone, Aadhaar…' },
  { value: 'caseId', label: 'Case ID', placeholder: 'Search by Case ID…' },
  { value: 'customerName', label: 'Customer Name', placeholder: 'Search by customer name…' },
  { value: 'phone', label: 'Phone No.', placeholder: 'Search by phone number…' },
  { value: 'aadhaar', label: 'Aadhaar No.', placeholder: 'Search by Aadhaar number…' },
  { value: 'pan', label: 'PAN', placeholder: 'Search by PAN…' },
  { value: 'customerCode', label: 'Customer ID', placeholder: 'Search by customer ID (AUK-…)…' },
];

export const EMPTY_FILTERS = {
  status: '',
  branch: '',
  dateRange: [null, null],
  metalType: '',
  paymentMode: '',
  agentId: '',
  minAmount: null,
  maxAmount: null,
};

const hasValue = (v) => v !== '' && v !== null && v !== undefined;

export const countActiveFilters = (f) =>
  ['status', 'branch', 'metalType', 'paymentMode', 'agentId'].filter((k) => hasValue(f[k])).length
  + (f.dateRange?.[0] ? 1 : 0)
  + (hasValue(f.minAmount) || hasValue(f.maxAmount) ? 1 : 0);

// Turn the filter state into API query params.
export const filtersToParams = (f) => {
  const params = {
    status: f.status,
    branch: f.branch,
    metalType: f.metalType,
    paymentMode: f.paymentMode,
    agentId: f.agentId,
    minAmount: hasValue(f.minAmount) ? f.minAmount : '',
    maxAmount: hasValue(f.maxAmount) ? f.maxAmount : '',
  };
  const [from, to] = f.dateRange || [];
  if (from?.isValid?.()) params.createdAfter = from.startOf('day').toISOString();
  if (to?.isValid?.()) params.createdBefore = to.endOf('day').toISOString();
  return params;
};

export function CaseSearch({ fields, field, onFieldChange, onSearch, size }) {
  const [text, setText] = useState('');
  const current = fields.find((f) => f.value === field) || fields[0];
  return (
    <Space.Compact style={{ minWidth: 340 }}>
      <Select
        value={field}
        onChange={onFieldChange}
        options={fields.map(({ value, label }) => ({ value, label }))}
        style={{ width: 140 }}
        size={size}
        popupMatchSelectWidth={false}
      />
      <Input.Search
        placeholder={current.placeholder}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (!e.target.value) onSearch(''); // cleared via the ✕ button
        }}
        onSearch={(v) => onSearch(v.trim())}
        enterButton={<SearchOutlined />}
        allowClear
        size={size}
      />
    </Space.Compact>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>{label}</label>
    {children}
  </div>
);

/**
 * Filter modal. Edits a local copy; `onApply` receives the new filters.
 * paymentModes: pass an options array to show the payment mode filter.
 */
export function CaseFilterModal({
  open, onClose, filters, onApply, isHO, branches = [], agents = [],
  statusOptions, statusLabel = (s) => s, paymentModes, amountLabel = 'Amount (₹)',
}) {
  const [temp, setTemp] = useState(filters);
  useEffect(() => { if (open) setTemp(filters); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (key) => (val) => setTemp((t) => ({ ...t, [key]: val ?? '' }));

  return (
    <Modal
      title="Filter Options"
      open={open}
      onOk={() => onApply(temp)}
      okText="Apply"
      onCancel={onClose}
      width={520}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {isHO && (
          <Field label="Branch">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="All Branches"
              style={{ width: '100%' }}
              value={temp.branch || undefined}
              onChange={set('branch')}
              options={branches.map((b) => ({ value: b._id, label: `${b.name}${b.code ? ` (${b.code})` : ''}` }))}
            />
          </Field>
        )}

        <Field label="Status">
          <Select
            allowClear
            placeholder="All Statuses"
            style={{ width: '100%' }}
            value={temp.status || undefined}
            onChange={set('status')}
            options={statusOptions.map((s) => ({ value: s, label: statusLabel(s) }))}
          />
        </Field>

        <Field label="Metal">
          <Select
            allowClear
            placeholder="Gold & Silver"
            style={{ width: '100%' }}
            value={temp.metalType || undefined}
            onChange={set('metalType')}
            options={[{ value: 'GOLD', label: 'Gold' }, { value: 'SILVER', label: 'Silver' }]}
          />
        </Field>

        {paymentModes && (
          <Field label="Payment Mode">
            <Select
              allowClear
              placeholder="All Payment Modes"
              style={{ width: '100%' }}
              value={temp.paymentMode || undefined}
              onChange={set('paymentMode')}
              options={paymentModes}
            />
          </Field>
        )}

        <Field label="Agent">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All Agents"
            style={{ width: '100%' }}
            value={temp.agentId || undefined}
            onChange={set('agentId')}
            options={agents.map((a) => ({ value: a._id, label: `${a.name}${a.agentId ? ` (${a.agentId})` : ''}` }))}
          />
        </Field>

        <Field label={amountLabel}>
          <Space.Compact style={{ width: '100%' }}>
            <InputNumber
              style={{ width: '50%' }}
              min={0}
              placeholder="Min"
              value={temp.minAmount}
              onChange={(v) => setTemp((t) => ({ ...t, minAmount: v }))}
            />
            <InputNumber
              style={{ width: '50%' }}
              min={0}
              placeholder="Max"
              value={temp.maxAmount}
              onChange={(v) => setTemp((t) => ({ ...t, maxAmount: v }))}
            />
          </Space.Compact>
        </Field>

        <Divider style={{ margin: 0 }} />

        <Field label="Created Date">
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            value={temp.dateRange?.[0] ? temp.dateRange : null}
            onChange={(dates) => setTemp((t) => ({ ...t, dateRange: dates && dates[0] ? dates : [null, null] }))}
            placeholder={['Start Date', 'End Date']}
            format="DD/MM/YYYY"
            presets={[
              { label: 'Today', value: [dayjs(), dayjs()] },
              { label: 'Last 7 days', value: [dayjs().add(-7, 'd'), dayjs()] },
              { label: 'Last 30 days', value: [dayjs().add(-30, 'd'), dayjs()] },
              { label: 'This month', value: [dayjs().startOf('month'), dayjs()] },
              { label: 'Last month', value: [dayjs().add(-1, 'month').startOf('month'), dayjs().add(-1, 'month').endOf('month')] },
            ]}
          />
        </Field>

        <Button block onClick={() => onApply(EMPTY_FILTERS)}>Clear All Filters</Button>
      </div>
    </Modal>
  );
}

// Removable chips summarising the applied filters, shown above the table.
export function ActiveFilterTags({
  filters, onChange, branches = [], agents = [], statusLabel = (s) => s, paymentModes = [], fmtAmount = (v) => v,
}) {
  const clear = (patch) => onChange({ ...filters, ...patch });
  const tags = [];
  if (filters.branch) {
    const b = branches.find((x) => x._id === filters.branch);
    tags.push({ key: 'branch', label: `Branch: ${b?.name || '…'}`, patch: { branch: '' } });
  }
  if (filters.status) tags.push({ key: 'status', label: `Status: ${statusLabel(filters.status)}`, patch: { status: '' } });
  if (filters.metalType) tags.push({ key: 'metal', label: `Metal: ${filters.metalType === 'GOLD' ? 'Gold' : 'Silver'}`, patch: { metalType: '' } });
  if (filters.paymentMode) {
    const pm = paymentModes.find((x) => x.value === filters.paymentMode);
    tags.push({ key: 'pm', label: `Payment: ${pm?.label || filters.paymentMode}`, patch: { paymentMode: '' } });
  }
  if (filters.agentId) {
    const a = agents.find((x) => x._id === filters.agentId);
    tags.push({ key: 'agent', label: `Agent: ${a?.name || '…'}`, patch: { agentId: '' } });
  }
  if (hasValue(filters.minAmount) || hasValue(filters.maxAmount)) {
    const lo = hasValue(filters.minAmount) ? fmtAmount(filters.minAmount) : 'any';
    const hi = hasValue(filters.maxAmount) ? fmtAmount(filters.maxAmount) : 'any';
    tags.push({ key: 'amt', label: `Amount: ${lo} – ${hi}`, patch: { minAmount: null, maxAmount: null } });
  }
  if (filters.dateRange?.[0]) {
    const [a, b] = filters.dateRange;
    tags.push({ key: 'date', label: `Created: ${a.format('DD/MM/YY')} – ${b ? b.format('DD/MM/YY') : '…'}`, patch: { dateRange: [null, null] } });
  }
  if (!tags.length) return null;
  return (
    <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {tags.map((t) => (
        <Tag key={t.key} closable color="blue" onClose={(e) => { e.preventDefault(); clear(t.patch); }}>
          {t.label}
        </Tag>
      ))}
      <Button type="link" size="small" onClick={() => onChange(EMPTY_FILTERS)}>Clear all</Button>
    </div>
  );
}
